import React, { useState, useRef, useEffect } from "react";

const CustomSearchDropdown = ({
  label = "",
  placeholder = "Select an option",
  name = "",
  value = "",
  onChange,
  options = [],
  optionValueKey = "id",
  optionLabelKey = "name",
  required = false,
  errorMessage = "This field is required",
  containerClassName = "",
  selectClassName = "",
  errorClassName = "",
  inlineLabel = false,
  disabled = false,
  showReset = false,
}) => {
  const [touched, setTouched] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isError = required && touched && !value;

  const handleReset = () => {
    onChange({ target: { name, value: "" } });
    setTouched(false);
    setSearchTerm("");
  };

  const filteredOptions = options.filter((item) => {
    const labelText = typeof item === "string" ? item : item[optionLabelKey];
    return labelText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const selectedLabel = options.find((item) =>
    typeof item === "string" ? item === value : item[optionValueKey] === value
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setTouched(true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className={`flex flex-col lg:flex-row space-y-5 lg:space-y-0 items-center lg:items-end w-5/6 ${containerClassName}`}
    >
      {label && (
        <span className="font-be-jost-pro font-medium text-white text-sm w-[15%]">
          {`${label}:`}
          {inlineLabel && ":"}
        </span>
      )}

      <div className="flex items-center w-full relative" ref={dropdownRef}>
        {/* Fake select box */}
        <div
          className={`bg-[#0c0b0b] ${
            disabled ? "text-gray-400 cursor-not-allowed" : "text-white"
          } border-b px-2 py-1 w-full outline-none border-b-[#E0E0E0] ${
            isError ? "border-b-red-500" : ""
          } ${selectClassName}`}
          onClick={() => !disabled && setOpen(!open)}
        >
          {selectedLabel
            ? typeof selectedLabel === "string"
              ? selectedLabel
              : selectedLabel[optionLabelKey]
            : `${placeholder} ${required ? "*" : ""}`}
        </div>

        {/* Reset button */}
        {showReset && value && !disabled && (
          <button
            type="button"
            onClick={handleReset}
            className="ml-2 text-xs text-gray-300 hover:text-white px-2 py-1 border border-gray-500 rounded absolute right-[-50px]"
          >
            Reset
          </button>
        )}

        {/* Dropdown list */}
        {open && !disabled && (
          <div className="absolute top-full left-0 w-full bg-[#0c0b0b] border border-[#E0E0E0] mt-1 z-50">
            {/* Search bar */}
            <input
              type="text"
              className="bg-[#0c0b0b] text-white px-2 py-1 w-full outline-none border-b border-b-[#E0E0E0]"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {/* Options */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((item, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      onChange({
                        target: {
                          name,
                          value:
                            typeof item === "string"
                              ? item
                              : item[optionValueKey],
                        },
                      });
                      setOpen(false);
                      setSearchTerm("");
                    }}
                    className="px-2 py-1 text-white hover:bg-gray-700 cursor-pointer"
                  >
                    {typeof item === "string" ? item : item[optionLabelKey]}
                  </div>
                ))
              ) : (
                <div className="px-2 py-1 text-gray-400">No results</div>
              )}
            </div>
          </div>
        )}
      </div>

      {isError && (
        <p className={`text-red-500 text-xs mt-1 ${errorClassName}`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default CustomSearchDropdown;
